/** Shared interface pieces: the window, the button, sprites on canvas, toasts, navigation. */

import { useEffect, useRef, type ReactNode } from 'react';

import { copy } from '../core/copy';
import { palette, biomeFor } from './art/palette';
import {
  BANNER,
  CHEST,
  EASEL,
  MARKER_DONE,
  SIGN,
  bake,
  heroColors,
  heroLookKey,
  heroPose,
  spriteColors,
  type PixelRows,
} from './art/sprites';
import { decodeLook, type Izgled } from '../core/look';
import { sfx } from './audio';
import { dismissToast, useStore, type Screen } from './store';

/* ------------------------------------------------------------------ window */

export function Win({
  title,
  tone,
  children,
  className = '',
}: {
  title?: string;
  tone?: 'dark' | 'gold';
  children: ReactNode;
  className?: string;
}) {
  return (
    <div className={`win ${tone ?? ''} ${className}`.trim()}>
      {title ? <span className="win-title">{title}</span> : null}
      {children}
    </div>
  );
}

/* ------------------------------------------------------------------ button */

type BtnProps = {
  children: ReactNode;
  onClick?: () => void;
  variant?: 'primary' | 'gold' | 'ghost';
  size?: 'small';
  disabled?: boolean;
  type?: 'button' | 'submit';
  title?: string;
};

export function Btn({ children, onClick, variant, size, disabled, type = 'button', title }: BtnProps) {
  return (
    <button
      type={type}
      title={title}
      className={`btn ${variant ?? ''} ${size ?? ''}`.trim()}
      disabled={disabled}
      onClick={
        onClick
          ? () => {
              sfx.confirm();
              onClick();
            }
          : undefined
      }
    >
      {children}
    </button>
  );
}

/* ------------------------------------------------------------------ sprites in the DOM */

export function Sprite({
  rows,
  scale = 2,
  colors = spriteColors,
  flip = false,
  label,
  cacheKey,
}: {
  rows: PixelRows;
  scale?: number;
  colors?: Record<string, string | undefined>;
  flip?: boolean;
  label?: string;
  cacheKey: string;
}) {
  const ref = useRef<HTMLCanvasElement>(null);

  useEffect(() => {
    const canvas = ref.current;
    if (!canvas) return;
    const baked = bake(cacheKey, rows, colors, scale, flip);
    canvas.width = baked.width;
    canvas.height = baked.height;
    const ctx = canvas.getContext('2d')!;
    ctx.imageSmoothingEnabled = false;
    ctx.clearRect(0, 0, canvas.width, canvas.height);
    ctx.drawImage(baked.canvas, 0, 0);
  }, [rows, scale, colors, flip, cacheKey]);

  return <canvas ref={ref} aria-hidden={label ? undefined : true} aria-label={label} role={label ? 'img' : undefined} />;
}

export const MARKER_ROWS: Record<string, PixelRows> = {
  lekcije: SIGN,
  vezba: EASEL,
  veliki: BANNER,
  riznica: CHEST,
  opciono: SIGN,
  done: MARKER_DONE,
};

export function NodeGlyph({ kind, done, scale = 2 }: { kind: string; done?: boolean; scale?: number }) {
  const key = done ? 'done' : kind;
  return (
    <Sprite
      rows={MARKER_ROWS[key] ?? SIGN}
      scale={scale}
      cacheKey={`glyph:${key}`}
    />
  );
}

/**
 * One character, standing.
 *
 * Takes either the stored integer or an unpacked appearance, and the second form is not a
 * convenience: the editor previews a character that has not been saved and therefore has no
 * integer yet. Everything else in the app holds the integer, so both spellings have to work or the
 * editor would have to invent a number for every keystroke.
 */
export function HeroPortrait({
  look,
  scale = 3,
  facing = 'down',
  souvenirs = [],
}: {
  look: number | Izgled;
  scale?: number;
  facing?: 'down' | 'up' | 'left' | 'right';
  /** Worn souvenirs. Onboarding passes none — nobody has earned anything yet. */
  souvenirs?: readonly string[];
}) {
  const character = typeof look === 'number' ? decodeLook(look) : look;
  const { key, rows, flip } = heroPose(facing, 'stand', character, souvenirs);
  const colors = heroColors(character);
  return (
    <Sprite
      rows={rows}
      colors={colors}
      scale={scale}
      flip={flip}
      cacheKey={`hero:${heroLookKey(character)}:${key}`}
    />
  );
}

/**
 * The big preview in the editor: your character, walking on the spot.
 *
 * It walks rather than standing, and that is the whole reason this component exists instead of a
 * larger `HeroPortrait`. A wardrobe screen showing a static doll is a form with a picture on it; a
 * character taking steps is the character you are about to walk around an island with, and half the
 * art in this game — the bob, the swinging arm, hair with volume in it — only exists in motion.
 *
 * The gait is the game's own four phases on the game's own clock, so what you see here is exactly
 * what the map draws. Two frames per phase would have been cheaper and it would have been a
 * different walk, which is the one thing a preview may never be.
 */
export function HeroStage({
  izgled,
  facing,
  souvenirs = [],
  scale = 6,
}: {
  izgled: Izgled;
  facing: 'down' | 'up' | 'left' | 'right';
  souvenirs?: readonly string[];
  scale?: number;
}) {
  const ref = useRef<HTMLCanvasElement>(null);
  const key = heroLookKey(izgled);
  const worn = souvenirs.join(',');

  useEffect(() => {
    const canvas = ref.current;
    if (!canvas) return;
    const ctx = canvas.getContext('2d')!;
    const colors = heroColors(izgled);
    let raf = 0;
    let start = 0;

    const draw = (now: number) => {
      if (!start) start = now;
      // 165 ms a tile is the engine's own step. Four phases to a cycle, two tiles to a stride.
      const step = Math.floor((now - start) / 165) % 4;
      const phase = (['contactA', 'passA', 'contactB', 'passB'] as const)[step]!;
      const pose = heroPose(facing, phase, izgled, souvenirs);
      const baked = bake(`stage:${key}:${pose.key}`, pose.rows, colors, scale, pose.flip);

      const dpr = Math.min(3, window.devicePixelRatio || 1);
      canvas.width = Math.round(baked.width * dpr);
      canvas.height = Math.round(baked.height * dpr);
      canvas.style.width = `${baked.width}px`;
      canvas.style.height = `${baked.height}px`;
      ctx.imageSmoothingEnabled = false;
      ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
      ctx.clearRect(0, 0, baked.width, baked.height);
      ctx.drawImage(baked.canvas, 0, 0);
      raf = requestAnimationFrame(draw);
    };

    raf = requestAnimationFrame(draw);
    return () => cancelAnimationFrame(raf);
    // `worn` and not `souvenirs`: the array arrives fresh from its parent on every render, so
    // depending on it restarts the loop — and therefore the walk — every time anything is clicked.
    // The character would twitch back to a standing frame on each choice, which is precisely the
    // opposite of what a moving preview is for.
  }, [key, facing, worn, scale]);

  return <canvas ref={ref} className="herostage" aria-hidden />;
}

/* ------------------------------------------------------------------ the scene strip */

/**
 * The small animated diorama above the SADA card: the character standing next to whatever the
 * next step is, on a strip of their current island, with the sea moving behind. It is decoration,
 * but it is the decoration that makes a task card read as a place you are standing in.
 */
export function SceneStrip({
  biome,
  kind,
  look,
  highlight,
  souvenirs = [],
}: {
  biome: string;
  kind: string;
  look: number;
  highlight?: 'gold' | null;
  /**
   * Worn souvenirs. This strip is the most-seen pixel of the product — the daily reminder that
   * the cap was earned lives here, not on a stats page.
   */
  souvenirs?: readonly string[];
}) {
  const ref = useRef<HTMLCanvasElement>(null);

  useEffect(() => {
    const canvas = ref.current;
    if (!canvas) return;
    const ctx = canvas.getContext('2d')!;
    let raf = 0;
    let t = 0;
    const theme = biomeFor(biome);
    const character = decodeLook(look);
    const heroColorMap = heroColors(character);
    const pose = heroPose('down', 'stand', character, souvenirs);
    const hero = bake(`scene:hero:${look}:${pose.key}`, pose.rows, heroColorMap, 1);
    const marker = bake(`scene:marker:${kind}`, MARKER_ROWS[kind] ?? SIGN, spriteColors, 1);

    const W = 160;
    const H = 44;

    const draw = () => {
      const dpr = Math.min(3, window.devicePixelRatio || 1);
      const rect = canvas.getBoundingClientRect();
      const scale = Math.max(1, Math.floor(rect.width / W));
      canvas.width = Math.round(rect.width * dpr);
      canvas.height = Math.round(rect.height * dpr);

      const buffer = document.createElement('canvas');
      buffer.width = W;
      buffer.height = H;
      const bx = buffer.getContext('2d')!;

      // Sky, sea, then a band of the island's own ground.
      bx.fillStyle = '#8fd8f2';
      bx.fillRect(0, 0, W, H);
      bx.fillStyle = palette.shallow;
      bx.fillRect(0, 16, W, 12);
      bx.fillStyle = palette.sea;
      bx.fillRect(0, 16, W, 4);
      bx.fillStyle = 'rgba(255,255,255,0.6)';
      for (let i = 0; i < 9; i++) {
        const x = (i * 19 + Math.floor(t / 90)) % (W + 18) - 9;
        bx.fillRect(x, 21 + (i % 2), 5, 1);
      }
      bx.fillStyle = theme.groundDark;
      bx.fillRect(0, 26, W, H - 26);
      bx.fillStyle = theme.ground;
      bx.fillRect(0, 28, W, H - 28);
      bx.fillStyle = theme.groundAlt;
      for (let i = 0; i < 22; i++) {
        const x = (i * 7 + (i % 3) * 3) % W;
        bx.fillRect(x, 31 + ((i * 5) % 10), 1, 1);
      }

      const bob = Math.round(Math.sin(t / 420) * 1);
      bx.drawImage(marker.canvas, 92, 14 + Math.round(Math.sin(t / 700)));
      bx.fillStyle = 'rgba(0,0,0,0.16)';
      bx.fillRect(58, 41, 10, 2);
      bx.drawImage(hero.canvas, 56, 20 + bob);

      if (highlight === 'gold') {
        bx.fillStyle = palette.gold;
        const pulse = 3 + Math.round(Math.sin(t / 300) * 2);
        bx.fillRect(97, 6 + pulse, 2, 2);
        bx.fillRect(95, 8 + pulse, 6, 1);
      }

      ctx.imageSmoothingEnabled = false;
      ctx.clearRect(0, 0, canvas.width, canvas.height);
      ctx.drawImage(buffer, 0, 0, W, H, 0, 0, canvas.width, canvas.height);
      void scale;
    };

    const loop = (time: number) => {
      t = time;
      draw();
      raf = requestAnimationFrame(loop);
    };
    raf = requestAnimationFrame(loop);
    const onResize = () => draw();
    window.addEventListener('resize', onResize);
    return () => {
      cancelAnimationFrame(raf);
      window.removeEventListener('resize', onResize);
    };
    // Keyed on the joined ids, not the array: callers pass fresh arrays on every render, and an
    // identity dep would tear down and restart the animation loop each time for the same outfit.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [biome, kind, look, highlight, souvenirs.join('+')]);

  return <canvas ref={ref} className="scene" aria-hidden="true" />;
}

/* ------------------------------------------------------------------ toasts */

export function Toasts() {
  const toasts = useStore((s) => s.toasts);
  if (toasts.length === 0) return null;
  return (
    <div className="toasts" role="status" aria-live="polite">
      {toasts.map((t) => (
        <div key={t.id} className={`toast ${t.tone}`} onClick={() => dismissToast(t.id)}>
          <div className="tt">{t.title}</div>
          {t.body ? <div className="bd">{t.body}</div> : null}
        </div>
      ))}
    </div>
  );
}

/* ------------------------------------------------------------------ modal */

export function Modal({
  title,
  onClose,
  children,
}: {
  title: string;
  onClose: () => void;
  children: ReactNode;
}) {
  const ref = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const onKey = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        sfx.cancel();
        onClose();
      }
      if (event.key !== 'Tab' || !ref.current) return;
      // Keep focus inside: this modal is where links get submitted, and tabbing out of it onto the
      // map behind would be quietly disastrous on a keyboard.
      const focusable = ref.current.querySelectorAll<HTMLElement>(
        'button:not([disabled]), a[href], input:not([disabled]), textarea, [tabindex]:not([tabindex="-1"])',
      );
      if (focusable.length === 0) return;
      const first = focusable[0]!;
      const last = focusable[focusable.length - 1]!;
      if (event.shiftKey && document.activeElement === first) {
        event.preventDefault();
        last.focus();
      } else if (!event.shiftKey && document.activeElement === last) {
        event.preventDefault();
        first.focus();
      }
    };
    document.addEventListener('keydown', onKey);
    const previous = document.activeElement as HTMLElement | null;
    ref.current?.querySelector<HTMLElement>('button, input')?.focus();
    return () => {
      document.removeEventListener('keydown', onKey);
      previous?.focus?.();
    };
  }, [onClose]);

  return (
    <div
      className="overlay"
      onMouseDown={(event) => {
        if (event.target === event.currentTarget) {
          sfx.cancel();
          onClose();
        }
      }}
    >
      <div className="modal" ref={ref} role="dialog" aria-modal="true" aria-label={title}>
        <div className="modal-head">
          <span>{title}</span>
          <button className="iconbtn" onClick={onClose} aria-label={copy.a11y.close}>
            ✕
          </button>
        </div>
        <div className="modal-body">{children}</div>
      </div>
    </div>
  );
}

/* ------------------------------------------------------------------ navigation */

/**
 * The screens that actually exist in this phase, in the order they appear.
 *
 * Exported because feature unlocks (rules.ts) run ahead of built screens: LIK still unlocks as a
 * feature the moment the first island clears and still has no screen of its own, and nothing should
 * announce a door that is not there yet. MOJE OSTRVO arrives at that same gate, under its own
 * feature name — the disclosure table has always promised something here.
 */
export const NAV_ORDER: Screen[] = ['sada', 'mapa', 'dnevnik', 'dom', 'posada'];

export function Nav({
  features,
  current,
  onGo,
  fresh,
}: {
  features: string[];
  current: Screen;
  onGo: (screen: Screen) => void;
  fresh: string[];
}) {
  const visible = NAV_ORDER.filter((screen) => features.includes(screen));
  return (
    <nav className="nav" aria-label="Glavna navigacija">
      {visible.map((screen) => (
        <button
          key={screen}
          className={`${current === screen ? 'on' : ''} ${fresh.includes(screen) ? 'fresh' : ''}`.trim()}
          aria-current={current === screen ? 'page' : undefined}
          onClick={() => onGo(screen)}
        >
          {copy.nav[screen as keyof typeof copy.nav]}
        </button>
      ))}
    </nav>
  );
}
